import * as React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { PERMISSION } from 'src/features/auth';
import { useGetParking } from 'src/features/parkingManagement/api/useGetParking.ts';
import { ICON_MAP } from 'src/features/parkingStatus/utils';
import { useListPoints } from 'src/features/points/api/useListPoints';
import { useGetPricingPlansPoints } from 'src/features/pricingPlans/api/useGetPricingPlansPoints';
import { EditPricingPlanByPoint } from 'src/features/pricingPlans/components/EditPricingPlanByPoint';
import { pricingPlansMessages } from 'src/features/pricingPlans/utils/messages';
import useHasPermissions from 'src/hooks/useHasPermissions.ts';
import {
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import DataTableColumnGenericHeaderDefinition from 'src/registry/components/table/DataTableColumnGenericHeaderDefinition';
import { Button } from 'src/registry/ui/Button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/registry/ui/Tooltip';
import { useTitleStore } from 'src/stores/useTitleStore.ts';
import { generalMessages } from 'src/utils/messages';

import type { PricingPlanPoint } from 'src/features/pricingPlans/types';

function ParkingPricingPlansByPoints() {
    const { parkingId } = useParams();
    const { formatMessage } = useIntl();
    const { setTitle, removeTitle } = useTitleStore();

    const [isEditModalOpen, setIsEditModalOpen] = React.useState<boolean>(false);
    const [selectedPricingPlanPoint, setSelectedPricingPlanPoint] = React.useState<PricingPlanPoint | undefined>(
        undefined,
    );

    const permissionPricingManage = useHasPermissions([PERMISSION.PRICING_MANAGE]);

    const { data } = useGetParking({ parkingId: parkingId || '', config: { enabled: !!parkingId } });

    const { data: parkingPoints } = useListPoints({
        parkingId: parkingId || '',
        config: { enabled: !!parkingId },
    });

    const {
        data: pricingPlansPoints,
        isLoading,
        isError,
    } = useGetPricingPlansPoints({
        parkingId: parkingId || '',
        config: { enabled: !!parkingId },
    });

    const onlyEntryPoints = parkingPoints?.results.filter((point) => point.type === 'entry') || [];
    const entryPointsIds = new Set(onlyEntryPoints.map((item) => item.id));

    const pricingPlanPointsEntryOnly = React.useMemo(() => {
        return pricingPlansPoints?.results.filter((point) => entryPointsIds.has(point.id)) || [];
    }, [pricingPlansPoints, entryPointsIds]);

    const pointsNameMapping = onlyEntryPoints.reduce<Record<string, string>>((acc, item) => {
        acc[item.id] = item.name;
        return acc;
    }, {});

    React.useEffect(() => {
        if (!data) return;
        setTitle(data.name);
        return () => removeTitle();
    }, [data, formatMessage]);

    const columnHelper = createColumnHelper<PricingPlanPoint>();

    const columns = [
        columnHelper.accessor('id', {
            header: DataTableColumnHeader,
            cell: ({ row }) => <div>{pointsNameMapping?.[row.original.id]}</div>,
            meta: {
                title: formatMessage(pricingPlansMessages.pricingPlansPoint),
            },
        }),
        columnHelper.accessor('price_plan_name', {
            header: DataTableColumnHeader,
            meta: {
                title: formatMessage(pricingPlansMessages.pricingPlan),
            },
        }),
        columnHelper.accessor('id', {
            header: DataTableColumnGenericHeaderDefinition,
            id: 'actions',
            cell: ({ row }) =>
                permissionPricingManage && (
                    <div className="flex w-full justify-end">
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        onClick={() => {
                                            setIsEditModalOpen(true);
                                            setSelectedPricingPlanPoint(row.original);
                                        }}
                                        size="icon"
                                        variant="link"
                                    >
                                        <FontAwesomeIcon icon={ICON_MAP.EDIT} className="cursor-pointer" />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent side="top">{formatMessage(generalMessages.edit)}</TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </div>
                ),
        }),
    ];

    return (
        <>
            <DataTable
                columns={columns}
                data={{
                    count: pricingPlanPointsEntryOnly.length,
                    results: pricingPlanPointsEntryOnly,
                }}
                semantics={{
                    title: formatMessage(pricingPlansMessages.pricingPlanEntryPoints),
                    entryName: formatMessage(pricingPlansMessages.pricingPlanEntryPoint),
                    entryNames: formatMessage(pricingPlansMessages.pricingPlanEntryPoints),
                }}
                isError={isError}
                isLoading={isLoading}
                components={{
                    controls: DataTableControls,
                    pagination: DataTablePagination,
                }}
            />
            {isEditModalOpen && selectedPricingPlanPoint && permissionPricingManage && (
                <EditPricingPlanByPoint
                    selectedPricingPlanPoint={selectedPricingPlanPoint}
                    isEditModalOpen={isEditModalOpen}
                    setIsEditModalOpen={setIsEditModalOpen}
                />
            )}
        </>
    );
}

export default ParkingPricingPlansByPoints;
